import { logOut } from 'stash/actions/session';
import {
	REFRESH_TOKEN_REQUEST,
	refreshTokenRequest,
	DEV_TOKEN_REQUEST,
} from 'stash/actions/api/auth';
import { isDev, checkExpired, formatAccessToken } from 'stash/utils';
import { getErrorMessage } from 'stash/utils/errors';
import { selectRefreshTokenPending } from 'stash/selectors/session';
import { getCookie } from 'stash/utils/cookies';
import {
	REFRESH_TOKEN_COOKIE_NAME,
	ACCESS_TOKEN_COOKIE_NAME,
} from 'stash/constants/session';

export const FETCH_REQUEST_WITH_TYPES = 'FETCH_REQUEST_WITH_TYPES';

// Creates an action which is interpreted by /middleware/fetch.js.
// The fetch middleware is passed action types and promises
// to dispatch them when the request is made.
export const fetchRequestWithTypes = (action) => {
	return {
		type: FETCH_REQUEST_WITH_TYPES,
		...action,
	};
};

const FETCH_MW_KEY = '__fetchMiddleware';

const isDevEnv = isDev();

export const MOCK_MODE = 'mockMode';
export const HTTP_MODE = 'same-origin';

// Validate that the FETCH_REQUEST_WITH_TYPES action has everything
// it needs to actually make the call and dispatch the three actions.

const validateFetch = ({ request, types }) => {
	if (!types || types.length !== 3) {
		throw new Error('Fetch: Expected three action types');
	}
	if (!request) {
		throw new Error('Fetch: No request key specified.');
	}
	if (!request.method) {
		throw new Error('Fetch: No HTTP method specified.');
	}
	if (!request.path) {
		throw new Error('Fetch: No URL path specified.');
	}
};

// The three actions which make up an async request (request, success, errored)
// are unpacked in the middleware and dispatched to Redux from here.

const dispatchFetchAction = (dispatch, type, { ...action }, response) => {
	return dispatch({
		...action,
		type,
		response,
	});
};

// Build the URL for the request. Allow for common params to be injected from
// state (such as :uuid)

export const getRequestURL = (apiKey, path) => {
	let newPath = path;

	if (apiKey && apiKey.user_id) {
		newPath = newPath.replace(':user_id', apiKey.user_id);
	}

	if (apiKey && apiKey.uuid) {
		newPath = newPath.replace(':uuid', apiKey.uuid);
	}

	return newPath;
};

export const handleFetchRequest = (url, options, action) => {
	if (options.mode === MOCK_MODE) {
		return Promise.resolve(action.mockResponse);
	}

	return fetch(url, options).then((res) => {
		if (res.status === 204) {
			return { success: true, status: 204 };
		}

		return res
			.json()
			.catch()
			.then((json) => {
				if (json instanceof Array || Array.isArray(json)) {
					return { body: json, status: res.status };
				} else {
					return { ...json, status: res.status };
				}
			});
	});
};

export const sendRequest = ({ url, options, dispatch, action, state }) => {
	// Make the HTTP request.
	return handleFetchRequest(url, options, action)
		.then((json) => {
			if (json.status >= 400) {
				throw json;
			}

			// Dispatch the "Success" action
			dispatchFetchAction(
				dispatch,
				action[FETCH_MW_KEY].successType,
				{ ...action, [FETCH_MW_KEY]: undefined },
				json
			);

			return Promise.resolve(json);
		})
		.catch((error) => {
			const displayError = getErrorMessage(error);

			// If the refresh token is expired, log out
			if (error.status === 400 && action.type === REFRESH_TOKEN_REQUEST) {
				dispatch(logOut());
			}

			// If we ever get a 401 from the API, log a user out immediately if in legacy auth.
			if (error.status === 401) {
				// If a refresh token exists, we are in auth V 2, so refresh the access token
				const token = getCookie(REFRESH_TOKEN_COOKIE_NAME);
				if (token) {
					//If the token is already being refreshed, retry the action and do not re-refresh
					if (selectRefreshTokenPending(state)) {
						dispatch(action);
					} else {
						dispatch(refreshTokenRequest(token)).then(() => {
							dispatch(action);
						});
					}
				} else {
					dispatch(logOut());
				}
			} else {
				// Dispatch the "Errored" action.
				dispatchFetchAction(
					dispatch,
					action[FETCH_MW_KEY].erroredType,
					{ ...action, [FETCH_MW_KEY]: undefined },
					error
				);
				const errorMessage = {
					...error,
					displayError,
				};

				throw errorMessage;
			}
		});
};

export const fetchMiddleware =
	({ dispatch, getState }) =>
	(next) =>
	async (action) => {
		if (action.type === FETCH_REQUEST_WITH_TYPES) {
			validateFetch(action);

			// Get the specified action types
			const [requestType, successType, erroredType] = action.types;

			// Dispatch the "Request" action immediately.
			return dispatchFetchAction(dispatch, requestType, {
				...action,
				[FETCH_MW_KEY]: { successType, erroredType },
			});
		}

		if (action[FETCH_MW_KEY]) {
			const state = getState();
			const apiKey = state.entities && state.entities.api_key;

			const path = getRequestURL(apiKey, action.request.path);
			const baseUrl = action.request.basePath
				? action.request.basePath
				: window.location.origin + '/apis';

			const url = `${baseUrl}${path}`;
			const liveMode =
				action.type === REFRESH_TOKEN_REQUEST || action.type === DEV_TOKEN_REQUEST
					? 'cors'
					: HTTP_MODE;
			const mode = isDevEnv && action.mockResponse ? MOCK_MODE : liveMode;
			let accessToken;
			if (action.type !== REFRESH_TOKEN_REQUEST) {
				accessToken = decodeURIComponent(getCookie(ACCESS_TOKEN_COOKIE_NAME));
			}

			const refreshToken = getCookie(REFRESH_TOKEN_COOKIE_NAME);
			if (refreshToken && checkExpired(accessToken)) {
				const newToken = await dispatch(refreshTokenRequest(refreshToken));
				accessToken = formatAccessToken(newToken.access_token);
			}

			const options = {
				mode,
				body:
					action.request.method !== 'GET'
						? JSON.stringify(action.request.body)
						: undefined,
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
					/**
					 * If the action is a refresh token request, do not include the access token
					 * or smaug will throw a 401 error if the token is expired.
					 */
					AUTHORIZATION:
						action.type === REFRESH_TOKEN_REQUEST ? undefined : accessToken || undefined,
					...action.request.headers,
				},
				method: action.request.method,
			};

			// Continue dispatching the request action
			next({ ...action, [FETCH_MW_KEY]: undefined });

			return sendRequest({ url, options, dispatch, action, state });
		}

		return next(action);
	};

export default fetchMiddleware;
